import { useState } from 'react';
import { api } from '../api.js';

// T087 — Conversational triage panel. Sits collapsed at the top of the
// Available section; operator clicks "✨ Triage with Claude" to spend
// one call and get a ranked top-5 + 2-3 hidden gems back. The rec
// rows themselves don't change — this is a separate decision support
// surface.

export default function TriagePanel({ aiKeyReady, recs, onJumpToRec }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const triage = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.post('/ai/triage', { limit: 20 });
      setResult(r);
    } catch (e) {
      setError(e?.body?.message || e?.message || 'Triage failed');
    } finally {
      setLoading(false);
    }
  };

  if (!aiKeyReady) return null;
  if (!recs || recs.length < 3) return null; // not worth a call

  const recById = new Map(recs.map((r) => [r.id, r]));

  return (
    <div className="mt-3 rounded-lg border border-slate-800 bg-slate-900/30 p-3">
      {!result && !error && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={triage}
            disabled={loading}
            className="rounded-md border border-brand-700 px-3 py-1 text-xs text-brand-400 hover:bg-brand-600/10 disabled:opacity-50"
          >
            {loading ? '✨ Ranking…' : "✨ Triage today's jobs"}
          </button>
          <span className="text-[11px] text-slate-500">
            Ranks the strongest 5 + flags 2-3 hidden gems. ~$0.02 per call.
          </span>
        </div>
      )}

      {error && (
        <div className="space-y-1">
          <p className="text-xs text-red-400">⚠ {error}</p>
          <button type="button" onClick={triage} className="rounded border border-slate-700 px-2 py-0.5 text-[10px] text-slate-400 hover:border-brand-600">Retry</button>
        </div>
      )}

      {result && (
        <div className="space-y-3">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-[10px] uppercase tracking-widest text-brand-400">Triage result</p>
            <button
              type="button"
              onClick={() => setResult(null)}
              className="rounded border border-slate-700 px-2 py-0.5 text-[10px] text-slate-400 hover:border-brand-600"
            >
              clear
            </button>
          </div>

          {result.topPicks?.length > 0 && (
            <div>
              <p className="text-[10px] font-medium uppercase tracking-wide text-emerald-400">Strongest fits</p>
              <ol className="mt-1 space-y-1">
                {result.topPicks.map((p) => {
                  const rec = recById.get(p.jobId);
                  return (
                    <li key={p.jobId} className="rounded border border-slate-800 bg-slate-950/40 p-2 text-xs">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-medium text-emerald-300">
                          #{p.rank} · {rec?.job?.title ?? '?'} @ {rec?.job?.company ?? '?'}
                        </span>
                        <button
                          type="button"
                          onClick={() => onJumpToRec?.(p.jobId)}
                          className="shrink-0 rounded border border-slate-700 px-1.5 py-0.5 text-[10px] text-slate-400 hover:border-brand-600 hover:text-brand-400"
                        >
                          Jump
                        </button>
                      </div>
                      <p className="mt-1 text-slate-300">{p.rationale}</p>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {result.hiddenGems?.length > 0 && (
            <div>
              <p className="text-[10px] font-medium uppercase tracking-wide text-amber-400">Hidden gems (with caveats)</p>
              <ol className="mt-1 space-y-1">
                {result.hiddenGems.map((g) => {
                  const rec = recById.get(g.jobId);
                  return (
                    <li key={g.jobId} className="rounded border border-slate-800 bg-slate-950/40 p-2 text-xs">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="font-medium text-amber-300">
                          {rec?.job?.title ?? '?'} @ {rec?.job?.company ?? '?'}
                        </span>
                        <button
                          type="button"
                          onClick={() => onJumpToRec?.(g.jobId)}
                          className="shrink-0 rounded border border-slate-700 px-1.5 py-0.5 text-[10px] text-slate-400 hover:border-brand-600 hover:text-brand-400"
                        >
                          Jump
                        </button>
                      </div>
                      <p className="mt-1 text-slate-300">{g.rationale}</p>
                      <p className="mt-0.5 italic text-amber-200/70">⚠ {g.caveat}</p>
                    </li>
                  );
                })}
              </ol>
            </div>
          )}

          {result.skipReason && (
            <p className="rounded border border-slate-800 bg-slate-950/40 p-2 text-[11px] text-slate-400">
              <strong>Skip:</strong> {result.skipReason}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
